import {
  Action,
  Alert,
  Card,
  Modal,
  OrderLineItem,
  RelativeDateTime,
} from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy, trackingStatusCodeName } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { ReactNode } from 'react';
import DescriptionList, { Description } from '~/components/DescriptionList';

export function ShipmentTrackingModal({
  shipment,
  show,
  onClose,
  renderLineItems,
  paginator,
}: {
  shipment: CrewMerchantUi.OrderShipment;
  show: boolean;
  onClose: () => void;
  paginator?: ReactNode;
  /** replaces the plain line item list, for callers that know more about the lines than the shipment does */
  renderLineItems?: (shipment: CrewMerchantUi.OrderShipment) => ReactNode;
}) {
  const { tracker, fulfilledFrom, lineItems } = shipment;
  const { carrier, status, estimatedDeliveryDate, details } = tracker;
  // ? Only show the tracking eye if there is more than one tracking detail
  const hasMoreThanOneDetail = shipment.tracker.details.length > 1;

  const deliveredOn = details.find((d) => d.status === 'delivered')?.date;

  const descriptions = [
    { term: 'Carrier', details: carrier.name },
    { term: 'Status', details: trackingStatusCodeName[status] },
    { term: 'Tracking Number', details: carrier.trackingCode },
    deliveredOn
      ? { term: 'Delivered On', details: formatter.date(deliveredOn) }
      : estimatedDeliveryDate && {
          term: 'Estimated Delivery',
          details: formatter.date(estimatedDeliveryDate),
        },
    fulfilledFrom && { term: 'Fulfilled From', details: fulfilledFrom },
  ].filter(isTruthy) satisfies Description[];

  return (
    <Modal title="Shipment Details" show={show} onClose={onClose}>
      {!hasMoreThanOneDetail && (
        <Alert heading="Limited Tracking History" variant="warning">
          <div className="flex items-center justify-between gap-2">
            <p>Verify the shipment status with the carrier.</p>
            <Action
              to={
                carrier.trackingUrl ??
                `https://www.google.com/search?q=${carrier.trackingCode}`
              }
              target="_blank"
              icon="externalLink"
            >
              View
            </Action>
          </div>
        </Alert>
      )}
      <div className="flex flex-col gap-4">
        {paginator}
        <DescriptionList descriptions={descriptions} />

        {renderLineItems ? (
          renderLineItems(shipment)
        ) : (
          <div className="flex flex-col gap-2">
            <Card.Heading>Line Items</Card.Heading>
            <ul className="space-y-3">
              {lineItems.map((lineItem) => (
                <li key={lineItem.id}>
                  <OrderLineItem
                    size="sm"
                    title={lineItem.name}
                    sku={lineItem.sku}
                    src={lineItem.imgUrl}
                    quantity={lineItem.quantity}
                    options={lineItem.optionsFromPlatform}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Card.Heading>Tracking History</Card.Heading>
          {details.length === 0 ? (
            <Card.Description>No tracking events yet.</Card.Description>
          ) : (
            <ul className="divide-y divide-neutral-200 text-xs">
              {details.map((event) => {
                const { city, state, country } = event.location;
                const location = [city, state, country]
                  .filter(isTruthy)
                  .join(', ');

                return (
                  <li
                    key={`${event.date}-${event.status}-${event.message}`}
                    className="flex items-start justify-between gap-2 py-2"
                  >
                    <div>
                      <p className="font-medium text-neutral-800">
                        {trackingStatusCodeName[event.status]}
                      </p>
                      <p className="text-neutral-500">{event.message}</p>
                      {location && (
                        <p className="text-neutral-500">{location}</p>
                      )}
                    </div>
                    <RelativeDateTime
                      dateTime={event.date}
                      className="shrink-0 text-neutral-500"
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
